import { getConfig } from "@/lib/config";
import { csrfToken, setCsrfToken } from "@/auth/csrf";
import { loginUrlFor } from "@/auth/login-url";
import { useAuthStore } from "@/stores/auth-store";

// Imperative auth side-effects the UI can call: redirect-to-login,
// logout. /auth/refresh is handled inline by the BFF's GraphQL proxy
// (see apps/bff/src/routes/graphql.ts) so the SPA rarely needs to
// call it manually.

export function redirectToLogin(next?: string): void {
  const target = next ?? window.location.pathname + window.location.search;
  window.location.assign(loginUrlFor(target));
}

// `next` is where the user lands after the NEXT successful login. It defaults
// to home, which is what an intentional logout wants. The session-expiry notice
// passes the page the user was on: there the logout is not the goal, only the
// teardown of a dead session before signing in again (#177).
export async function logout(next = "/"): Promise<void> {
  const cfg = getConfig();
  const token = csrfToken();
  if (!token) {
    // No CSRF token means no session; logout is a no-op locally.
    useAuthStore.getState().setAnonymous();
    redirectToLogin(next);
    return;
  }
  try {
    await fetch(`${cfg.basePath}auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "x-csrf-token": token },
    });
  } catch {
    // Even if the BFF call fails, clear local state - the worst case
    // is a stranded refresh token at Keycloak, not a security issue.
  }
  setCsrfToken(null);
  useAuthStore.getState().setAnonymous();
  redirectToLogin(next);
}
